import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { ConfirmingRequestDocument, SUBPRODUCT_LABELS } from 'lib/types/models';

import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';
import { ChatMessage } from '../SimulationExportDialog/components/ChatMessagesDialog';
import { ConfirmingExportRequest } from '../SimulationExportDialog/types/ConfirmingExportRequest';

interface JsPDFWithAutoTable extends jsPDF {
  lastAutoTable?: {
    finalY: number;
  };
}

export interface ConfirmingSimulationReportConfig extends PDFGeneratorConfig {
  selectedRequest: ConfirmingExportRequest;
  showClientInfo?: boolean;
  showRequestInfo?: boolean;
  showDates?: boolean;
  showAdvance?: boolean;
  showRate?: boolean;
  showArrearRate?: boolean;
  showAmounts?: boolean;
  showDocuments?: boolean;
  showOperationCosts?: boolean;
  showExpenses?: boolean;
  showAccountReceivable?: boolean;
  payouts?: unknown;
}

export class ConfirmingSimulationReportGenerator extends BasePDFGenerator {
  private selectedRequest: ConfirmingExportRequest;
  protected config: ConfirmingSimulationReportConfig;
  private payouts?: unknown;

  constructor(config: ConfirmingSimulationReportConfig) {
    super({ ...config, orientation: 'landscape' });
    this.selectedRequest = config.selectedRequest;
    this.config = config;
    this.payouts = config.payouts;
  }

  protected addSummarySection(): void {
    const selectedRequest = this.selectedRequest;
    const operationCostTotal =
      (selectedRequest.operationCostComissionFixed ||
        selectedRequest.operationCostFixed ||
        0) +
      (selectedRequest.operationCostComissionVariable ||
        selectedRequest.operationCostVariable ||
        0);
    const operationCostWithIVA = operationCostTotal * 1.19;
    const expensesWithIVA = selectedRequest.expensesHasIva
      ? (selectedRequest.expensesCost || 0) * 1.19
      : selectedRequest.expensesCost || 0;

    this.doc.setFontSize(10);
    const col1X = 10;
    const col2X = 115;
    const col3X = 210;
    const defaultX = 38;
    const initialY = 30;

    // Verificar configuraciones de visibilidad
    const showClientInfo = this.config.showClientInfo !== false;
    const showRequestInfo = this.config.showRequestInfo !== false;
    const showDates = this.config.showDates !== false;
    const showAdvance = this.config.showAdvance !== false;
    const showRate = this.config.showRate !== false;
    const showArrearRate = this.config.showArrearRate !== false;
    const showAmounts = this.config.showAmounts !== false;
    const showOperationCosts = this.config.showOperationCosts !== false;
    const showExpenses = this.config.showExpenses !== false;
    const showAccountReceivable = this.config.showAccountReceivable !== false;

    // Columna 1 - Información del cliente
    let currentY = initialY;

    if (showClientInfo) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Razón Social', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      const shortName = getShortString(selectedRequest.stakeholderName, 25);
      this.doc.text(`: ${shortName}`, col1X + defaultX, currentY);
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Rut Cliente', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${selectedRequest.stakeholderIdentifier}`,
        col1X + defaultX,
        currentY,
      );
      currentY += 5;
    }

    if (showRequestInfo) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Ejecutivo', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${selectedRequest.createdBy?.name || 'N/A'}`,
        col1X + defaultX,
        currentY,
      );
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Nro. Solicitud', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.id ? selectedRequest.id : 'N/A')}`,
        col1X + defaultX,
        currentY,
      );
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Tipo Producto', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${SUBPRODUCT_LABELS[selectedRequest.subProductId as keyof typeof SUBPRODUCT_LABELS] || 'Confirming'}`,
        col1X + defaultX,
        currentY,
      );
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Moneda', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(': CLP', col1X + defaultX, currentY);
      currentY += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Estado', col1X, currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`: ${selectedRequest.status}`, col1X + defaultX, currentY);
    }

    // Columna 2 - Fechas y parámetros
    let col2Y = initialY;

    if (showDates) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Fecha Operación', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${formatDate(new Date(selectedRequest.operationDate))}`,
        col2X + defaultX,
        col2Y,
      );
      col2Y += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Fecha Creación', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${formatDate(new Date(selectedRequest.createdAt))}`,
        col2X + defaultX,
        col2Y,
      );
      col2Y += 5;

      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Fecha Financiamiento', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${selectedRequest.financingAt ? formatDate(new Date(selectedRequest.financingAt)) : 'No ejecutado'}`,
        col2X + defaultX,
        col2Y,
      );
      col2Y += 5;
    }

    if (showAdvance) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Anticipo', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.advance || 0)}%`,
        col2X + defaultX,
        col2Y,
      );
      col2Y += 5;
    }

    if (showRate) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Diferencia Precio', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.rate || 0)}%`,
        col2X + defaultX,
        col2Y,
      );
      col2Y += 5;
    }

    if (showArrearRate && selectedRequest.arrearRate) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Tasa Mora', col2X, col2Y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.arrearRate)}%`,
        col2X + defaultX,
        col2Y,
      );
    }

    // Columna 3 - Montos
    const alignRight = (text: string, x: number, y: number) => {
      const textWidth = this.doc.getTextWidth(text);
      this.doc.text(':', x + 35 - 25, y);
      this.doc.text(text, x + 35 - textWidth, y);
    };

    let col3Y = initialY;

    if (showRate) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Diferencia Precio', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      const totalCostRate = (selectedRequest.documents || []).reduce(
        (acc: number, doc: ConfirmingRequestDocument) => {
          const daysToCount = doc.daysToCount || 0;
          const rate = selectedRequest.rate || 0;
          const exchangeDays = Number(selectedRequest.exchangeDays || 0);
          const amount = doc.amount || 0;
          return (
            acc + ((daysToCount + exchangeDays) / 30) * (rate / 100) * amount
          );
        },
        0,
      );
      alignRight(`${formatMoney(totalCostRate)}`, col3X + defaultX, col3Y);
      col3Y += 5;
    }

    if (showOperationCosts) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Comisión + IVA', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(operationCostWithIVA)}`,
        col3X + defaultX,
        col3Y,
      );
      col3Y += 5;
    }

    if (
      showExpenses &&
      selectedRequest.expensesCost &&
      selectedRequest.expensesCost > 0
    ) {
      const expensesText = selectedRequest.expensesHasIva
        ? 'Gastos + IVA'
        : 'Gastos';
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(expensesText, col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(`${formatMoney(expensesWithIVA)}`, col3X + defaultX, col3Y);
      col3Y += 5;
    }

    if (showAccountReceivable) {
      // Cuenta por Cobrar
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Cuenta por Cobrar', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      const totalCostRate = (selectedRequest.documents || []).reduce(
        (acc: number, doc: ConfirmingRequestDocument) => {
          const daysToCount = doc.daysToCount || 0;
          const rate = selectedRequest.rate || 0;
          const exchangeDays = Number(selectedRequest.exchangeDays || 0);
          const amount = doc.amount || 0;
          return (
            acc + ((daysToCount + exchangeDays) / 30) * (rate / 100) * amount
          );
        },
        0,
      );
      const accountReceivable =
        totalCostRate + operationCostWithIVA + expensesWithIVA;
      alignRight(`${formatMoney(accountReceivable)}`, col3X + defaultX, col3Y);
      col3Y += 5;
    }

    if (showAmounts) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Monto a Girar', col3X, col3Y);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(selectedRequest.amount)}`,
        col3X + defaultX,
        col3Y,
      );
    }
  }

  protected addContentSection(): void {
    if (this.config.showDocuments !== false) {
      this.addDocumentsTable();
    }
    if (this.config.showAccountReceivable !== false) {
      this.addPendingAccountsTable();
    }
  }

  private addDocumentsTable(): void {
    const selectedRequest = this.selectedRequest;
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Documentos', 10, 75);
    this.doc.setFont('helvetica', 'normal');

    autoTable(this.doc, {
      startY: 80,
      head: [
        [
          'Nro. Doc',
          'Rut Proveedor',
          'Razón Social',
          'Tipo',
          'Sub Tipo',
          'Monto',
          'Fecha Emisión',
          'Fecha Vencimiento',
          'Días',
        ],
      ],
      body: (selectedRequest.documents || [])
        .filter(
          (doc: ConfirmingRequestDocument) => doc.type !== 'Cuenta por cobrar',
        )
        .map((doc: ConfirmingRequestDocument) => [
          String(doc.folio || 'N/A'),
          doc.businessIdentifier || 'N/A',
          getShortString(doc.businessName || 'N/A', 20),
          doc.type || 'N/A',
          doc.subType || 'N/A',
          formatMoney(doc.amount || 0),
          formatDate(new Date(doc.issuedDate)),
          doc.paymentDate ? formatDate(new Date(doc.paymentDate)) : 'N/A',
          String(doc.daysToCount || 0),
        ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  private addPendingAccountsTable(): void {
    const selectedRequest = this.selectedRequest;
    const pendingAccountDocs = (selectedRequest.documents || []).filter(
      (doc: ConfirmingRequestDocument) => doc.type === 'Cuenta por cobrar',
    );

    if (pendingAccountDocs.length === 0) {
      return;
    }

    const lastTable = (this.doc as JsPDFWithAutoTable).lastAutoTable;
    const titleY = lastTable ? lastTable.finalY + 15 : 75;

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Cuenta por Cobrar Generada', 10, titleY);
    this.doc.setFont('helvetica', 'normal');

    const bodyRows: any[] = pendingAccountDocs.map(
      (doc: ConfirmingRequestDocument) => [
        String(doc.folio || 'N/A'),
        doc.type || 'N/A',
        formatDate(new Date(doc.issuedDate)),
        formatMoney(doc.amount || 0),
      ],
    );

    autoTable(this.doc, {
      startY: titleY + 5,
      head: [['Nro. Doc', 'Tipo', 'Fecha de Emisión', 'Monto']],
      body: bodyRows,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 30 }, // Nro. Doc
        1: { cellWidth: 35 }, // Tipo
        2: { cellWidth: 40 }, // Fecha de Emisión
        3: { cellWidth: 35 }, // Monto
      },
    });
  }

  protected async addTitleSection(): Promise<void> {
    const { userName, imageBase64, title, orientation } = this.config;
    const imgX = 10,
      imgY = 5;
    let imgHeight = 15;

    const { width: naturalWidth, height: naturalHeight } =
      await this.getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    let imgWidth = imgHeight * aspectRatio;
    const maxWidth = 60;
    if (imgWidth > maxWidth) {
      imgWidth = maxWidth;
      imgHeight = imgWidth / aspectRatio;
    }

    this.doc.addImage(imageBase64, 'PNG', imgX, imgY, imgWidth, imgHeight);

    const pageWidth = orientation === 'landscape' ? 297 : 210;

    // Calcular el centro del espacio disponible (después de la imagen)
    const imageEndX = imgX + imgWidth;
    const availableWidth = pageWidth - imageEndX;
    const titleCenterX = imageEndX + availableWidth / 2;

    // Centrar el título en el espacio disponible
    this.drawText(title, titleCenterX, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });

    // Para confirming, posicionar fecha y usuario alineados con los valores de la columna derecha
    const rightX = orientation === 'landscape' ? 283 : 283;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
      align: 'right',
    });
    this.drawText(`Usuario: ${userName}`, rightX, 15, {
      size: 10,
      align: 'right',
    });
  }

  public async generate() {
    await this.addTitleSection();
    this.addSummarySection();
    this.addContentSection();
    if (this.payouts) {
      this.doc.addPage();
      await this.addPayoutsSection(this.payouts);
    }
    // Agregar sección de observaciones del chat si está habilitada
    if (
      this.selectedRequest.showChatAsObservations &&
      this.selectedRequest.selectedChatMessages &&
      this.selectedRequest.selectedChatMessages.length > 0
    ) {
      this.doc.addPage();
      await this.addObservationsSection(
        this.selectedRequest.selectedChatMessages,
      );
    }
    this.saveDocument();
  }

  public async addPayoutsSection(payoutGroup: any): Promise<void> {
    if (!payoutGroup || !Array.isArray(payoutGroup.payments)) return;
    await this.addTitleSectionCustom('Pagos Realizados');
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    const payments = payoutGroup.payments;
    autoTable(this.doc, {
      startY: 30,
      head: [
        [
          'Banco Origen',
          'N° Cuenta Origen',
          'Banco Destino',
          'N° Cuenta Destino',
          'RUT Destino',
          'Tipo Cuenta Destino',
          'Titular Cuenta Destino',
          'Giro',
          'Monto',
        ],
      ],
      body:
        payments.length > 0
          ? payments.map((p: any) => [
              p.bankAccountOriginName,
              p.bankAccountOriginNumber,
              p.bankAccountDestinationName,
              p.bankAccountDestinationNumber,
              p.bankAccountDestinationHolderIdentifier,
              p.bankAccountDestinationAccountType,
              p.bankAccountDestinationHolderName,
              p.executedAt ? formatDate(p.executedAt) : '',
              formatMoney(p.amount),
            ])
          : [
              [
                {
                  content: 'No hay pagos realizados',
                  colSpan: 9,
                  styles: { halign: 'center', fontStyle: 'italic' },
                },
              ],
            ],
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 35 },
        6: { cellWidth: 30 },
        7: { cellWidth: 25 },
        8: { cellWidth: 30 },
      },
    });
  }

  private async addTitleSectionCustom(title: string): Promise<void> {
    const { userName, imageBase64, orientation } = this.config;
    const imgX = 10,
      imgY = 5,
      imgHeight = 15;
    const { width: naturalWidth, height: naturalHeight } =
      await this.getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    const imgWidth = imgHeight * aspectRatio;
    this.doc.addImage(imageBase64, 'PNG', imgX, imgY, imgWidth, imgHeight);
    const pageWidth = orientation === 'landscape' ? 297 : 210;
    this.drawText(title, pageWidth / 2, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });
    const rightX = orientation === 'landscape' ? 285 : 285;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
      align: 'right',
    });
    this.drawText(`Usuario: ${userName}`, rightX, 15, {
      size: 10,
      align: 'right',
    });
  }

  private async addObservationsSection(messages: ChatMessage[]): Promise<void> {
    await this.addTitleSectionCustom('Observaciones');

    const pageWidth = this.doc.internal.pageSize.getWidth();
    const pageHeight = this.doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 30;
    const bottomMargin = 20;
    const boxWidth = pageWidth - 2 * marginX;
    const maxBoxHeight = pageHeight - marginY - bottomMargin;
    const boxHeight = Math.min(180, maxBoxHeight);
    const padding = 5;

    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.5);
    this.doc.rect(marginX, marginY, boxWidth, boxHeight);

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');

    let currentY = marginY + padding + 5;
    const textWidth = boxWidth - 2 * padding;
    const lineHeight = 6;

    messages.forEach((message) => {
      const timestamp = formatDate(message.timestamp);
      const userInfo = `${message.userName}${
        message.userRole ? ` (${message.userRole})` : ''
      }`;
      const header = `[${timestamp}] ${userInfo}:`;

      this.doc.setFont('helvetica', 'bold');
      const headerLines = this.doc.splitTextToSize(header, textWidth);
      this.doc.text(headerLines, marginX + padding, currentY);
      currentY += headerLines.length * lineHeight;

      this.doc.setFont('helvetica', 'normal');
      const textLines = this.doc.splitTextToSize(message.text, textWidth);
      this.doc.text(textLines, marginX + padding, currentY);
      currentY += textLines.length * lineHeight + 2;
    });
  }
}
